import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Send, Check, CheckCheck, Pencil, Trash2, Briefcase, Building2, Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 5000;

const formatTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
};

const formatDay = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const y = new Date(); y.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Oggi";
  if (d.toDateString() === y.toDateString()) return "Ieri";
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
};

const Tick = ({ status }) => {
  if (status === "read") return <CheckCheck className="h-3.5 w-3.5 text-sky-300" data-testid="tick-read" />;
  if (status === "delivered") return <CheckCheck className="h-3.5 w-3.5 text-slate-300" data-testid="tick-delivered" />;
  if (status === "sent") return <Check className="h-3.5 w-3.5 text-slate-300" data-testid="tick-sent" />;
  return null;
};

export const ChatPanel = ({ conversationId, partnerName, partnerType, currentUser, onMessagesChanged }) => {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingBody, setEditingBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const isAdmin = currentUser?.role === "admin";

  const loadMessages = useCallback(async (markRead = true) => {
    if (!conversationId) return;
    try {
      const { data } = await api.get(`/chat/conversations/${conversationId}/messages`);
      setMessages(data);
      if (markRead) {
        const hasUnread = data.some((m) => m.sender_user_id !== currentUser?.id && !m.deleted);
        if (hasUnread) {
          await api.post(`/chat/conversations/${conversationId}/read`);
          onMessagesChanged?.();
        }
      }
    } catch (e) { toast.error(formatApiError(e)); }
  }, [conversationId, currentUser?.id, onMessagesChanged]);

  useEffect(() => {
    loadMessages(true);
    const t = setInterval(() => loadMessages(true), POLL_MS);
    return () => clearInterval(t);
  }, [loadMessages]);

  // auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await api.post(`/chat/conversations/${conversationId}/messages`, { body: text });
      setDraft("");
      await loadMessages(false);
      onMessagesChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const saveEdit = async () => {
    const newText = editingBody.trim();
    if (!newText) return;
    try {
      await api.put(`/chat/messages/${editingId}`, { body: newText });
      setEditingId(null);
      setEditingBody("");
      await loadMessages(false);
      onMessagesChanged?.();
      toast.success("Messaggio modificato");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeMsg = async (id) => {
    if (!window.confirm("Eliminare definitivamente questo messaggio?")) return;
    try {
      await api.delete(`/chat/messages/${id}`);
      await loadMessages(false);
      onMessagesChanged?.();
      toast.success("Messaggio eliminato");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // group messages by day
  const groups = [];
  let lastDay = null;
  for (const m of messages) {
    const day = formatDay(m.created_at);
    if (day !== lastDay) {
      groups.push({ kind: "day", day });
      lastDay = day;
    }
    groups.push({ kind: "msg", m });
  }

  const Icon = partnerType === "broker" ? Briefcase : Building2;

  return (
    <>
      {/* Chat header */}
      <header className="px-5 py-3 border-b border-slate-200 flex items-center gap-3 bg-white sticky top-0 z-10">
        <div className={`h-9 w-9 rounded-full flex items-center justify-center ${
          partnerType === "broker" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"
        }`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate" data-testid="chat-partner-name">{partnerName}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wide">
            {partnerType === "broker" ? "Broker / Agenzia" : "Compagnia"}
          </div>
        </div>
      </header>

      {/* Messages list */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-5 py-4 space-y-2 scrollbar-thin"
        style={{ background: "linear-gradient(180deg, #F1F5F9 0%, #F8FAFC 100%)", maxHeight: "calc(70vh - 130px)" }}
        data-testid="chat-messages"
      >
        {groups.length === 0 && (
          <div className="text-center text-xs text-slate-400 py-12">Nessun messaggio ancora. Scrivi il primo!</div>
        )}
        {groups.map((g, i) => {
          if (g.kind === "day") {
            return (
              <div key={`d-${i}`} className="flex justify-center my-3">
                <span className="text-[10px] uppercase tracking-wider bg-white border border-slate-200 text-slate-500 px-3 py-1 rounded-full">{g.day}</span>
              </div>
            );
          }
          const m = g.m;
          const mine = m.sender_user_id === currentUser?.id;
          const isEditing = editingId === m.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"} group`} data-testid={`msg-${m.id}`}>
              <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm shadow-sm relative ${
                m.deleted
                  ? "bg-slate-100 text-slate-400 italic border border-dashed border-slate-200"
                  : mine
                  ? "bg-blue-600 text-white"
                  : "bg-white border border-slate-200 text-slate-800"
              }`}>
                {!mine && !m.deleted && (
                  <div className="text-[10px] font-semibold text-emerald-700 mb-0.5">{m.sender_name}</div>
                )}
                {m.deleted ? (
                  <span>🚫 Messaggio eliminato</span>
                ) : isEditing ? (
                  <div className="space-y-1.5">
                    <textarea
                      data-testid={`edit-textarea-${m.id}`}
                      value={editingBody}
                      onChange={(e) => setEditingBody(e.target.value)}
                      rows={2}
                      className="w-full text-sm text-slate-800 bg-white border border-slate-200 rounded px-2 py-1 resize-y"
                    />
                    <div className="flex gap-1 justify-end">
                      <button data-testid={`cancel-edit-${m.id}`} onClick={() => { setEditingId(null); setEditingBody(""); }} className="text-[11px] text-slate-200 hover:text-white px-1.5 py-0.5 rounded">Annulla</button>
                      <button data-testid={`save-edit-${m.id}`} onClick={saveEdit} className="text-[11px] bg-white text-blue-600 font-semibold px-2 py-0.5 rounded hover:bg-blue-50">Salva</button>
                    </div>
                  </div>
                ) : (
                  <div className="whitespace-pre-wrap break-words">{m.body}</div>
                )}
                {!m.deleted && !isEditing && (
                  <div className={`flex items-center gap-1 mt-1 ${mine ? "justify-end text-blue-100" : "justify-end text-slate-400"}`}>
                    {m.edited && <span className="text-[9px] italic mr-1">modificato</span>}
                    <span className="text-[10px]">{formatTime(m.created_at)}</span>
                    {mine && <Tick status={m.status} />}
                  </div>
                )}
                {/* Admin actions */}
                {isAdmin && !m.deleted && !isEditing && (
                  <div className={`absolute -top-2 ${mine ? "-left-2" : "-right-2"} hidden group-hover:flex items-center gap-1 bg-white border border-slate-200 rounded-full shadow-md px-1 py-0.5`}>
                    <button
                      data-testid={`edit-msg-${m.id}`}
                      onClick={() => { setEditingId(m.id); setEditingBody(m.body); }}
                      className="text-slate-500 hover:text-blue-600 p-1"
                      title="Modifica"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button
                      data-testid={`delete-msg-${m.id}`}
                      onClick={() => removeMsg(m.id)}
                      className="text-slate-500 hover:text-red-600 p-1"
                      title="Elimina"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <footer className="border-t border-slate-200 bg-white p-3 flex items-end gap-2">
        <textarea
          data-testid="chat-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          rows={1}
          placeholder="Scrivi un messaggio… (Invio per inviare, Shift+Invio per andare a capo)"
          className="flex-1 resize-none px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 max-h-32"
          style={{ minHeight: "40px" }}
        />
        <button
          data-testid="chat-send"
          onClick={send}
          disabled={sending || !draft.trim()}
          className="h-10 w-10 flex-shrink-0 rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white flex items-center justify-center transition-colors"
        >
          <Send className="h-4 w-4" />
        </button>
      </footer>
    </>
  );
};
